// Copyright (c) StrangeBee 2023

import { UserController, UserOrganisationInput, UserEditOptions, UserInput, UserOutput } from 'thehive-sdk';

export interface UserEventList {
    onCreate: (user: UserInput) => Promise<UserOutput | void>;
    onSetPassword: (password: string, userId: string) => Promise<void>;
    onChangePassword: (currentPassword: string, password: string, userId: string) => Promise<void>;
    onEdit: (editedUser: UserEditOptions, userId: string) => Promise<void>;
    onDelete: (userId: string, orgId?: string) => Promise<void>;
    onUnblock: (userId: string) => Promise<void>;
    onCreateAPIKey: (userId: string) => Promise<string | void>;
    onRevokeAPIKey: (userId: string) => Promise<void>;
    onGetAPIKey: (userId: string) => Promise<string>;
    onSwitchOrganisation: (organisation: string) => Promise<UserOutput>;
    onGetCurrent: () => Promise<UserOutput>;
    onSetOrganisations: (organisations: UserOrganisationInput[], userId: string) => Promise<void>;
}

interface UserEventListParams {
    locale: (label: string) => string;
    controller: UserController;
    onSuccess?: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
}

export const computeEventList = ({ locale, controller, onSuccess, onError }: UserEventListParams): UserEventList => ({
    onCreate: (user) =>
        controller
            .create<UserInput, UserOutput>(user)
            .then((userCreated) => {
                onSuccess?.(false, locale('notification.user.create'));
                return userCreated;
            })
            .catch(onError),
    onEdit: (editedUser, userId: string) =>
        controller
            .edit<UserEditOptions, void>(editedUser, userId)
            .then(() => onSuccess?.(false, locale('notification.user.edit')))
            .catch(onError),
    onSetPassword: (password, userId: string) => controller.setPassword(password, userId),
    onChangePassword: (currentPassword, password, userId) =>
        controller.changePassword(currentPassword, password, userId),
    onDelete: (userId, orgId) =>
        orgId
            ? controller
                  .delete(userId, orgId)
                  .then(() => onSuccess?.(true))
                  .catch(onError)
            : controller
                  .forceDelete(userId)
                  .then(() => onSuccess?.(true))
                  .catch(onError),
    onGetAPIKey: async (userId: string): Promise<string> => {
        return controller.getAPIKey(userId);
    },
    onUnblock: (login): Promise<any> => {
        return controller
            .unBlock(login)
            .then(() => onSuccess?.(false, locale('notification.user.unblock')))
            .catch(onError);
    },
    onCreateAPIKey: (userId: string) =>
        controller
            .renewAPIKey(userId)
            .then((apiKey) => {
                onSuccess?.(false, locale('notification.APIKey.create'));
                return apiKey;
            })
            .catch(onError),
    onRevokeAPIKey: (userId: string) =>
        controller
            .revokeAPIKey(userId)
            .then(() => onSuccess?.(false, locale('notification.APIKey.revoke')))
            .catch(onError),
    onSwitchOrganisation: (organisation) => controller.switchOrganisation(organisation),
    onGetCurrent: () => controller.getCurrent(),
    onSetOrganisations: (organisations, userId) => controller.setOrganisations(organisations, userId).catch(onError),
});
